import { useRouter } from "next/router";

import { useSellerPayouts } from "@/Apollo/hooks/useSellerPayouts";
import { Currency } from "@/Apollo/schema";
import {
  formatAccountDate,
  formatAccountMoney,
} from "@/common/utils/account-format.utils";
import { usePaymentCopy } from "@/components/Account/payment/usePaymentCopy";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

/** Stripe timestamps arrive as ISO strings or unix seconds; both become ISO. */
export const payoutCreatedIso = (created: unknown): string | null => {
  if (typeof created === "number") {
    return new Date(created * 1000).toISOString();
  }

  if (typeof created === "string" && created) {
    return created;
  }

  return null;
};

/** Date and amount of the last payouts to the bank. Never an expected arrival date. */
export const PayoutsList = () => {
  const { copy } = usePaymentCopy();
  const { locale } = useRouter();
  const { data, loading } = useSellerPayouts();
  const items = data?.payouts?.items ?? [];

  if (loading && !data) {
    return null;
  }

  return (
    <section
      aria-labelledby="payment-payouts-title"
      className="flex flex-col gap-3"
    >
      <Heading as="h2" size="h5" id="payment-payouts-title">
        {copy("sectionRecentPayouts")}
      </Heading>
      {items.length === 0 ? (
        <Body as="p" color="muted">
          {copy("payoutsEmpty")}
        </Body>
      ) : (
        <ul className="flex flex-col divide-y divide-grey-200">
          {items.map((payout, index) => {
            const created = payoutCreatedIso(payout.created);

            return (
              <li
                key={`${created ?? index}-${payout.amount}`}
                className="flex items-baseline justify-between gap-4 py-2"
              >
                <Body as="span">
                  {created ? formatAccountDate(created, locale) : ""}
                </Body>
                <Body as="span" className="tabular-nums">
                  {formatAccountMoney(payout.amount, {
                    locale,
                    currency:
                      (payout.currency?.toUpperCase() as Currency) ??
                      Currency.Eur,
                    place: "accounting",
                  })}
                </Body>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};
